import React from 'react';
import PropTypes from 'prop-types';
import Field from 'zignal-landscape/lib/Field';
import Label from 'zignal-landscape/lib/Label';
import Select from 'zignal-landscape/lib/Select';
import DateRangePicker from 'zignal-landscape/lib/DateRangePicker';

import DashboardTemplateItems from './DashboardTemplateItems';
import DashboardSelectedProfiles from './DashboardSelectedProfiles';
import DashboardTitleInput from './DashboardTitleInput';

import Styles from './DashboardSettingsModal.css';

const DashboardSettingsForm = ({
  dashboards,
  dateRanges,
  focused,
  hasTemplateChoices,
  isNew,
  isSubmitted,
  onSubmit,
  profileOptions,
  setValue,
  shouldFocus,
  templateTypes,
  timezone,
  scrollContainerRef,
  typeLabel,
  typeLabelPlural,
  values
}) => {
  const isCustomDateRange = values.defaultDateRange?.value === 'custom';
  const customStartEndDates = isCustomDateRange && values.customDateRangeStart && values.customDateRangeEnd
    ? { startDate: values.customDateRangeStart, endDate: values.customDateRangeEnd }
    : null;

  return (
    <React.Fragment>
      <DashboardTitleInput
        isSubmitted={isSubmitted}
        onChange={(value) => setValue('title', value)}
        onFocused={() => focused('title')}
        onSubmit={onSubmit}
        shouldFocus={shouldFocus.title}
        value={values.title}
        typeLabel={typeLabel}
      />

      {hasTemplateChoices && (
        <DashboardTemplateItems
          dashboards={dashboards}
          duplicateFrom={values.duplicateFrom}
          onChange={(value) => setValue('templateType', value)}
          onChangeDuplicateFrom={(value) => setValue('duplicateFrom', value)}
          templateTypes={templateTypes}
          typeLabel={typeLabel}
          typeLabelPlural={typeLabelPlural}
          value={values.templateType}
        />
      )}

      <DashboardSelectedProfiles
        allProfilesSelected={values.allProfilesSelected}
        isSubmitted={isSubmitted}
        isNew={isNew}
        onChangeAllProfilesSelected={(value) => {
          setValue('allProfilesSelected', value);
          setValue('profiles', value? null : [...profileOptions]);
        }}
        onChange={(value) => {
          setValue('profiles', value);
          setValue('defaultProfile', null);
        }}
        profiles={profileOptions}
        value={values.profiles}
      />

      <div className={Styles.selectInput}>
        <Field>
          <Label>Default Dataset</Label>
          <Select
            isClearable
            menuPosition={'fixed'}
            menuShouldBlockScroll
            onChange={(value) => setValue('defaultProfile', value)}
            options={values.profiles || profileOptions}
            placeholder={'No default set'}
            value={values.defaultProfile}
            selectStyles={{
              singleValue: base => ({
                ...base,
                padding: 0,
                lineHeight: 'inherit'
              })
            }}
          />
        </Field>
      </div>

      <div className={Styles.selectInput}>
        <Field>
          <Label>Default Date Range</Label>
          <Select
            isClearable
            menuPosition={'fixed'}
            menuShouldBlockScroll
            onChange={(value) => {
              setValue('defaultDateRange', value);
              if (!value) {
                setValue('customDateRangeStart', null);
                setValue('customDateRangeEnd', null);
              }
            }}
            options={dateRanges}
            placeholder={'No default set'}
            value={values.defaultDateRange}
            selectStyles={{
              singleValue: base => ({
                ...base,
                padding: 0,
                lineHeight: 'inherit'
              })
            }}
          />
        </Field>
      </div>
      {isCustomDateRange && (
        <div className={Styles.customDateRangeWrapper}>
          <DateRangePicker
            showNow={false}
            timezone={timezone}
            startEndDates={customStartEndDates || undefined}
            getPopupContainer={() => scrollContainerRef?.current || document.body}
            onChange={({ startDate, endDate }) => {
              setValue('customDateRangeStart', startDate);
              setValue('customDateRangeEnd', endDate);
            }}
          />
        </div>
      )}
    </React.Fragment>
  );
};

DashboardSettingsForm.propTypes = {
  dashboards: PropTypes.array,
  dateRanges: PropTypes.array,
  dispatch: PropTypes.func,
  focused: PropTypes.func,
  hasTemplateChoices: PropTypes.bool,
  isNew: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  onSubmit: PropTypes.func,
  profileOptions: PropTypes.array,
  setValue: PropTypes.func,
  shouldFocus: PropTypes.any,
  state: PropTypes.any,
  templateTypes: PropTypes.array,
  timezone: PropTypes.string,
  scrollContainerRef: PropTypes.object,
  typeLabel: PropTypes.string,
  typeLabelPlural: PropTypes.string,
  values: PropTypes.any
};

export default DashboardSettingsForm;
